import { useEffect, useState } from "react";
import { supabase } from "../utils/supabaseClient";

const money = (n) => `£${Number(n).toFixed(2)}`;

const formatUpdated = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
    : "";

// Edits the single row in the delivery_settings table (see migration-v4-delivery.sql).
// Only admins can save: the database policy enforces it, not just this page.
export default function DeliverySettingsForm() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flatFee, setFlatFee] = useState("");
  const [freeEnabled, setFreeEnabled] = useState(false);
  const [freeOver, setFreeOver] = useState("");
  const [saved, setSaved] = useState(null); // the values currently stored, to know if anything changed
  const [updatedAt, setUpdatedAt] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  function applyRow(row) {
    const free = row.free_over == null ? null : Number(row.free_over);
    setFlatFee(Number(row.flat_fee).toFixed(2));
    setFreeEnabled(free !== null);
    setFreeOver(free !== null ? free.toFixed(2) : "");
    setSaved({ flat: Number(row.flat_fee), free });
    setUpdatedAt(row.updated_at);
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const { data, error: loadError } = await supabase
        .from("delivery_settings")
        .select("flat_fee, free_over, updated_at")
        .eq("id", 1)
        .maybeSingle();

      if (cancelled) return;
      if (loadError || !data) {
        console.error("Loading delivery settings failed:", loadError);
        setError("We couldn't load the delivery settings. Check that the delivery SQL has been run.");
      } else {
        applyRow(data);
      }
      setLoading(false);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const flat = Number(flatFee);
  const free = Number(freeOver);
  const flatValid = flatFee !== "" && Number.isFinite(flat) && flat >= 0 && flat <= 999;
  const freeValid =
    !freeEnabled || (freeOver !== "" && Number.isFinite(free) && free > 0 && free <= 99999);

  const dirty =
    saved !== null &&
    (Math.round(flat * 100) !== Math.round(saved.flat * 100) ||
      (freeEnabled ? Math.round(free * 100) : null) !==
        (saved.free === null ? null : Math.round(saved.free * 100)));

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setNotice("");

    if (!flatValid) {
      setError("Enter a delivery charge between £0 and £999.");
      return;
    }
    if (!freeValid) {
      setError("Enter the order amount above which delivery is free, or turn free delivery off.");
      return;
    }

    setSaving(true);

    const { data, error: saveError } = await supabase
      .from("delivery_settings")
      .update({
        flat_fee: Math.round(flat * 100) / 100,
        free_over: freeEnabled ? Math.round(free * 100) / 100 : null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", 1)
      .select("flat_fee, free_over, updated_at")
      .maybeSingle();

    setSaving(false);

    // No error but no row back means the database refused the change (not an admin)
    if (saveError || !data) {
      console.error("Saving delivery settings failed:", saveError);
      setError("We couldn't save the changes. Check that you're signed in as an admin.");
      return;
    }

    applyRow(data);
    setNotice("Delivery settings saved. They apply to new checkouts straight away.");
  }

  let preview = "";
  if (flatValid && freeValid) {
    preview =
      flat === 0
        ? "Delivery is free on every order."
        : `Customers pay ${money(flat)} for delivery.`;
    if (flat > 0 && freeEnabled) {
      preview += ` Delivery is free on orders of ${money(free)} or more.`;
    }
  }

  return (
    <section className="adm-card" aria-labelledby="delivery-settings-title">
      <div className="adm-card-head">
        <h3 id="delivery-settings-title">Delivery settings</h3>
        {updatedAt && <span className="adm-muted">Last updated {formatUpdated(updatedAt)}</span>}
      </div>

      <div className="adm-card-body">
        {loading ? (
          <p className="adm-muted">Loading delivery settings...</p>
        ) : !saved ? (
          error && <p className="ui-alert ui-alert--error" role="alert">{error}</p>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            {error && (
              <p className="ui-alert ui-alert--error" role="alert">{error}</p>
            )}
            {notice && (
              <p className="ui-alert ui-alert--ok" role="status">{notice}</p>
            )}

            <div className="adm-form-grid">
              <div className="ui-field">
                <label className="ui-label" htmlFor="ds-fee">Delivery charge</label>
                <div className="ui-money">
                  <span aria-hidden="true">£</span>
                  <input
                    id="ds-fee"
                    className="ui-input"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={flatFee}
                    onChange={(e) => setFlatFee(e.target.value)}
                  />
                </div>
                <p className="ui-hint">Charged once per order. Enter 0 for free delivery on everything.</p>
              </div>

              <div className="ui-field">
                <label className="ui-check" htmlFor="ds-free-on">
                  <input
                    id="ds-free-on"
                    type="checkbox"
                    checked={freeEnabled}
                    onChange={(e) => setFreeEnabled(e.target.checked)}
                  />
                  Offer free delivery above a certain order value
                </label>

                <div className="ui-money">
                  <span aria-hidden="true">£</span>
                  <input
                    id="ds-free-over"
                    className="ui-input"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    placeholder="e.g. 50.00"
                    aria-label="Free delivery on orders of at least"
                    disabled={!freeEnabled}
                    value={freeOver}
                    onChange={(e) => setFreeOver(e.target.value)}
                  />
                </div>
                <p className="ui-hint">Orders of this amount or more (items only) get free delivery.</p>
              </div>
            </div>

            {preview && <p className="adm-preview">{preview}</p>}

            <div className="adm-form-actions" style={{ maxWidth: 320 }}>
              <button
                type="submit"
                className="ui-btn ui-btn--primary"
                disabled={saving || !dirty || !flatValid || !freeValid}
              >
                {saving ? "Saving..." : "Save delivery settings"}
              </button>
            </div>

            <p className="ui-hint" style={{ marginTop: "0.9rem" }}>
              Existing orders keep the delivery charge they were placed with. Customers who are
              already on the checkout page see the old price until they reload it, but the new
              price is what gets charged.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}